import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { featuredMovies, formatViews } from "@/lib/movies";

export default function HeroCarousel() {
  const total = featuredMovies.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const current = featuredMovies[index];

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const move = (step: number) => {
    setIndex((prev) => (prev + step + total) % total);
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-background-100"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="精选影视作品推荐"
    >
      <div className="absolute inset-0">
        <img
          key={current.id}
          src={current.still}
          alt={`${current.title}精选剧照`}
          title={`${current.title}剧情介绍`}
          className="w-full h-full object-cover object-top animate-fade-in"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/45"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-background-50/70"></div>
      </div>

      <div className="relative max-w-shell mx-auto w-full px-4 md:px-8 pt-24 md:pt-32 pb-12 md:pb-16">
        <div className="flex flex-col gap-1">
          <span className="font-label text-[11px] tracking-[0.4em] text-accent-500 uppercase">
            Film Archive · Since 2021
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 tracking-wide">
            淫乱小镇<span className="text-accent-500">.</span>
          </h1>
          <p className="text-xs md:text-sm text-foreground-500 tracking-[0.18em]">
            影视作品剧情介绍与深度解析
          </p>
        </div>

        <div className="mt-8 md:mt-14 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-8 lg:gap-14 items-center">
          <div key={current.id} className="animate-fade-up min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-[11px] font-medium">
                本周焦点
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-300 text-[11px]">
                {current.type}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-300 text-[11px]">
                {current.year}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-300 text-[11px]">
                {current.country}
              </span>
            </div>

            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-50 leading-tight">
              {current.title}
            </h2>

            <p className="mt-3 md:mt-4 text-sm md:text-lg text-accent-500 font-medium">
              {current.tagline}
            </p>

            <p className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-foreground-400 line-clamp-3">
              {current.summary}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5 text-accent-500 font-label text-base">
                <i className="ri-star-fill text-sm"></i>
                {current.rating.toFixed(1)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-star-line"></i>
                导演 {current.director}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatViews(current.views)} 阅读
              </span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="h-11 px-6 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-foreground-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                阅读剧情解析
              </Link>
              <Link
                to="/category"
                className="h-11 px-6 inline-flex items-center justify-center gap-2 rounded-md border border-background-300 text-foreground-200 text-sm hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line"></i>
                浏览全部作品
              </Link>
            </div>
          </div>

          <Link
            to={`/detail/${current.id}`}
            className="hidden lg:block group relative rounded-lg overflow-hidden border border-background-300/70 aspect-[2/3] w-full cursor-pointer"
            aria-label={`查看${current.title}详情`}
          >
            <img
              src={current.poster}
              alt={`${current.title}海报`}
              title={`${current.title}剧情介绍与分析`}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="font-label text-[10px] tracking-[0.3em] text-accent-500">FEATURED</p>
              <p className="mt-1 text-sm text-foreground-100">{current.genres.join(" / ")}</p>
            </div>
          </Link>
        </div>

        <div className="mt-8 md:mt-12 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            {featuredMovies.map((movie, dotIndex) => (
              <button
                key={movie.id}
                type="button"
                onClick={() => setIndex(dotIndex)}
                aria-label={`切换到${movie.title}`}
                className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                  dotIndex === index
                    ? "w-10 bg-accent-500"
                    : "w-4 bg-background-400 hover:bg-background-500"
                }`}
              ></button>
            ))}
            <span className="ml-2 font-label text-xs tracking-widest text-foreground-500">
              {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => move(-1)}
              aria-label="上一部精选作品"
              className="w-9 h-9 md:w-10 md:h-10 rounded-md border border-background-300 flex items-center justify-center text-foreground-300 hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <button
              type="button"
              onClick={() => move(1)}
              aria-label="下一部精选作品"
              className="w-9 h-9 md:w-10 md:h-10 rounded-md border border-background-300 flex items-center justify-center text-foreground-300 hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}